/*
 * File: string-helper.ts
 * File Created: 2023-12-25 02:12:02
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-02-21 17:28:54
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import lodash from "lodash";
import { AnyHelper } from "./any-helper";

export class StringHelper {
    static toNumber(value: string | undefined): number | null {
        if (value == undefined) return null;
        /// trim the spaces
        value = value.trim();
        /// handle string with m/mb
        let result = parseUnitString(value);

        if (typeof result === "string") {
            /// handle pretty format
            value = value.replace(/,/g, "");
            /// handle multiple negative
            value = value.replace(/^[\-]+/, "-");
            /// parse the number
            result = parseFloat(value);
        }
        if (result == null) return null;
        if (isNaN(result)) return null;

        /// handle percent
        if (/\%$/.test(value)) result /= 100;
        return result;
    }

    static parseUnitString(
        input: string | number | undefined | null,
        options?: { base?: 1000 | 1024 },
    ): number | null {
        return parseUnitString(input, options);
    }

    static formatUnitString(
        bytes: number | undefined | null,
        options?: {
            base?: 1000 | 1024;
            unit?: "B" | "KB" | "MB" | "GB" | "TB";
            precision?: number;
        },
    ): string | null {
        return formatUnitString(bytes, options);
    }

    static toBoolean(value: string): boolean | null {
        return AnyHelper.toBoolean(value);
    }

    static toDate(value: string, dateEnd: boolean = false): Date | null {
        /// detect the value of Date signature, if not a Date, return undefined
        // Date Signature:
        // 1) ex: 1970, 2023. it should be in range of 1970 to 3000.
        // 2) ex: 1970/1, 2023/05. it could be year + month of these formats.
        // 3) ex: 1970/1/1, 2023/05/01. it could be year + month + day of these formats.

        const yearRegex = /^(19[7-9]\d|2\d{3}|3000)$/;
        const yearMonthRegex = /^(19[7-9]\d|2\d{3}|3000)\/(0?[1-9]|1[0-2])$/;
        const fullDateRegex =
            /^(19[7-9]\d|2\d{3}|3000)\/(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])$/;

        let date: Date;

        if (yearRegex.test(value)) {
            date = dateEnd
                ? new Date(`${value}-12-31T23:59:59.999`)
                : new Date(`${value}-01-01`);
        } else if (yearMonthRegex.test(value)) {
            date = new Date(`${value}-01`);
            if (dateEnd) {
                // Move to the last day of the month
                date.setMonth(date.getMonth() + 1, 0);
                date.setHours(23, 59, 59, 999);
            }
        } else if (fullDateRegex.test(value)) {
            date = new Date(value.replace(/\//g, "-"));
            if (dateEnd) {
                date.setHours(23, 59, 59, 999);
            }
        } else {
            return null;
        }

        return date;
    }

    static toEval(input: string) {
        try {
            return eval(`(${input})`);
        } catch (e) {
            return null;
        }
    }

    static toObject(input: string, transformDate: boolean = true) {
        let result = this.toEval(input);
        if (result == null || typeof result !== "object") return null;

        /// transform date
        return transformDate ? transformObjectDates(result) : result;
    }

    static padLeft(input: string | number, digits: number, letter: string = "0"): string {
        if (typeof input === "number") input = input + "";
        return input.length >= digits
            ? input
            : Array(digits - input.length)
                  .fill(letter)
                  .join("") + input;
    }

    static padRight(
        input: string | number,
        digits: number,
        letter: string = "0",
    ): string {
        if (typeof input === "number") input = input + "";
        return input.length >= digits
            ? input
            : input +
                  Array(digits - input.length)
                      .fill(letter)
                      .join("");
    }

    /// erase the serial number of `1.` `a.`
    static eraseSerial(value: string): string {
        const regex = /^[0-9a-z]+\.\s*/i;
        return value.replace(regex, "");
    }

    /// case converter
    static capitalize(input: string) {
        // if (!value) return value; // Return the original string if it's empty or null

        // // Capitalize the first character and append the rest of the string
        // return value.charAt(0).toUpperCase() + value.slice(1);
        //return lodash.capitalize(lodash.camelCase(input));
        return lodash.upperFirst(lodash.camelCase(input));
    }
    static camelize(input: string) {
        return lodash.camelCase(input);
    }
    static dasherize(input: string) {
        return lodash.kebabCase(input);
    }
    static pascalize(input: string) {
        input = this.camelize(input);
        return input?.[0]?.toUpperCase() + input?.slice(1, input?.length ?? 0);
    }

    /// plural converter
    static pluralize(input: string): string {
        // Common irregular plurals
        const irregulars = {
            child: "children",
            woman: "women",
            man: "men",
            person: "people",
            mouse: "mice",
            goose: "geese",
            foot: "feet",
            tooth: "teeth",
            fish: "fish",
            sheep: "sheep",
        };

        // Reverse lookup for irregular plurals to check if the word is already plural
        const singularFromIrregulars = Object.entries(irregulars).reduce(
            (acc, [singular, plural]) => {
                acc[plural] = singular;
                return acc;
            },
            {} as { [key: string]: string },
        );

        // Return the input if it's already an irregular plural
        if (singularFromIrregulars[input]) {
            return input;
        }

        // Return the irregular plural if it exists
        if (irregulars[input]) {
            return irregulars[input];
        }

        // Check if the word is already plural by common pluralization rules
        if (
            input.endsWith("ies") || // Words like "cities"
            input.endsWith("es") || // Words like "buses", "boxes"
            (input.endsWith("s") && !input.endsWith("ss")) // Avoid words like "glass"
        ) {
            return input;
        }

        // Rules for regular pluralization
        if (
            input.endsWith("y") &&
            !input.endsWith("ay") &&
            !input.endsWith("ey") &&
            !input.endsWith("iy") &&
            !input.endsWith("oy") &&
            !input.endsWith("uy")
        ) {
            // If a noun ends in -y with a consonant before y, change the 'y' to 'ies'
            return input.slice(0, -1) + "ies";
        } else if (
            input.endsWith("s") ||
            input.endsWith("sh") ||
            input.endsWith("ch") ||
            input.endsWith("x") ||
            input.endsWith("z")
        ) {
            // If a noun ends in -s, -sh, -ch, -x, or -z, add 'es'
            return input + "es";
        } else {
            // In most other cases, simply add 's'
            return input + "s";
        }
    }

    /// unpluralize function to convert plural nouns to their singular form
    static unpluralize(input) {
        // Common irregular plurals
        const irregulars = {
            children: "child",
            women: "woman",
            men: "man",
            people: "person",
            mice: "mouse",
            geese: "goose",
            feet: "foot",
            teeth: "tooth",
            fish: "fish",
            sheep: "sheep",
        };

        // Return the singular form if it exists in the irregulars
        if (irregulars[input]) {
            return irregulars[input];
        }

        // Rules for regular depluralization
        if (input.endsWith("ies")) {
            // If a noun ends in -ies, change 'ies' back to 'y'
            return input.slice(0, -3) + "y";
        } else if (input.endsWith("es")) {
            // If a noun ends in -es after s, sh, ch, x, or z, remove 'es'
            const base = input.slice(0, -2);
            if (
                base.endsWith("s") ||
                base.endsWith("sh") ||
                base.endsWith("ch") ||
                base.endsWith("x") ||
                base.endsWith("z")
            ) {
                return base;
            }
        }

        // If a noun ends in -s, remove the 's'
        if (input.endsWith("s")) {
            return input.slice(0, -1);
        }

        // If none of the conditions match, assume the input is already singular
        return input;
    }

    /// replace the original content by mapping
    static replace(
        content: string,
        map: any,
        keyConverter: { (key: string): string } = (key) => `{{ ${key} }}`,
    ) {
        Object.keys(map).forEach((key) => {
            const value = map[key];
            key = keyConverter != null ? keyConverter(key) : key;
            content = content.replaceAll(key, value);
        });
        return content;
    }

    static toDocxString(value?: string | string[] | null) {
        if (value == null) return "";

        return (typeof value === "string" ? value.split(/\r?\n/) : value)
            .map((str) => `<w:t xml:space="preserve">${str}</w:t>`)
            .join("<w:br/>");
    }
}

function parseUnitString(
    input: string | number | undefined | null,
    options?: { base?: 1000 | 1024 },
): number | null {
    if (input == null) return null;
    if (typeof input === "number") return Number.isFinite(input) ? input : null;

    const base = options?.base ?? 1024;
    const value = input.trim();
    if (value === "") return null;

    const match = value
        .replace(/,/g, "")
        .match(/^(-?\d+(?:\.\d+)?)(?:\s*([kmgt]?b?|bytes?))?$/i);
    if (!match) return null;

    const num = Number(match[1]);
    if (!Number.isFinite(num)) return null;

    const unitRaw = (match[2] ?? "b").toLowerCase();
    const unit = unitRaw.replace(/^bytes?$/, "b");

    const units: Record<string, number> = {
        b: 1,
        kb: base,
        k: base,
        mb: base ** 2,
        m: base ** 2,
        gb: base ** 3,
        g: base ** 3,
        tb: base ** 4,
        t: base ** 4,
    };

    const factor = units[unit];
    if (!factor) return null;
    return num * factor;
}

function formatUnitString(
    bytes: number | undefined | null,
    options?: {
        base?: 1000 | 1024;
        unit?: "B" | "KB" | "MB" | "GB" | "TB";
        precision?: number;
    },
): string | null {
    if (bytes == null) return null;
    if (!Number.isFinite(bytes)) return null;

    const base = options?.base ?? 1024;
    const precision = options?.precision ?? 2;
    const units = ["B", "KB", "MB", "GB", "TB"] as const;
    const fixedUnit = options?.unit;

    let unitIndex = 0;
    let value = bytes;

    if (fixedUnit) {
        unitIndex = units.indexOf(fixedUnit);
        if (unitIndex < 0) return null;
        value = bytes / base ** unitIndex;
    } else {
        while (value >= base && unitIndex < units.length - 1) {
            value /= base;
            unitIndex += 1;
        }
    }

    const rounded =
        precision <= 0 ? Math.round(value).toString() : value.toFixed(precision);
    const trimmed = rounded.replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
    return `${trimmed}${units[unitIndex]}`;
}

function transformObjectDates(body: any): any {
    // Function to check if a string is a date
    function isDateString(value: string): boolean {
        const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;
        return dateRegex.test(value);
    }

    // Function to recursively transform the body
    function transform(value: any): any {
        if (Array.isArray(value)) {
            return value.map(transform);
        } else if (value && typeof value === "object") {
            const transformed = {};
            for (const key in value) {
                transformed[key] = transform(value[key]);
            }
            return transformed;
        } else if (typeof value === "string" && isDateString(value)) {
            return new Date(value);
        }
        return value;
    }

    return transform(body);
}
